import { BatteryFull, Info, Signal, Wifi, X } from "lucide-react";
import React, { useEffect, useState } from "react";
import { Constants } from "../../constant";
import {
  DEFAULT_STATUS_BANNER_COLOR,
  normalizeHexColor,
} from "src/lib/versionControlService";

const slabIndigo = "#6366F1";

/** Matches mobile `Constants.backendSlowMessage` when banner message is blank. */
const DEFAULT_BANNER_MESSAGE =
  "We're experiencing slower than usual response times. Thanks for your patience.";

const inputClass =
  "min-h-[48px] w-full rounded-2xl border-2 border-[#E5E5E5] bg-white px-4 py-2.5 text-[15px] font-semibold text-[#3C3C3C] shadow-[inset_0_2px_0_rgba(0,0,0,0.04)] placeholder:font-medium placeholder:text-[#AFAFAF] focus:border-[#6366F1] focus:outline-none focus:ring-4 focus:ring-[#6366F1]/25 disabled:cursor-not-allowed disabled:opacity-60";

const labelClass =
  "flex flex-col gap-2 text-sm font-extrabold uppercase tracking-wide text-[#777777]";

function formatClockTime(date: Date): string {
  return date.toLocaleTimeString(undefined, {
    hour: "numeric",
    minute: "2-digit",
  });
}

function contrastText(hex: string): "#000000" | "#FFFFFF" {
  const normalized = normalizeHexColor(hex);
  const r = parseInt(normalized.slice(1, 3), 16) / 255;
  const g = parseInt(normalized.slice(3, 5), 16) / 255;
  const b = parseInt(normalized.slice(5, 7), 16) / 255;
  // Relative luminance (sRGB), same idea as Flutter `computeLuminance`.
  const toLinear = (c: number) =>
    c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  const luminance =
    0.2126 * toLinear(r) + 0.7152 * toLinear(g) + 0.0722 * toLinear(b);
  return luminance > 0.45 ? "#000000" : "#FFFFFF";
}

export type SystemStatusBannerSectionProps = {
  step?: number;
  disabled?: boolean;
  enabled: boolean;
  message: string;
  color: string;
  onEnabledChange: (enabled: boolean) => void;
  onMessageChange: (message: string) => void;
  onColorChange: (color: string) => void;
};

/** Step UI for mobile system status banner fields on the app release panel. */
export const SystemStatusBannerSection: React.FC<
  SystemStatusBannerSectionProps
> = ({
  step = 3,
  disabled = false,
  enabled,
  message,
  color,
  onEnabledChange,
  onMessageChange,
  onColorChange,
}) => {
  const pickerDisabled = disabled || !enabled;
  const swatch = normalizeHexColor(color || DEFAULT_STATUS_BANNER_COLOR);
  const previewMessage = message.trim() || DEFAULT_BANNER_MESSAGE;
  const previewText = contrastText(swatch);
  const [clock, setClock] = useState(() => formatClockTime(new Date()));

  useEffect(() => {
    const tick = () => setClock(formatClockTime(new Date()));
    tick();
    const id = window.setInterval(tick, 30_000);
    return () => window.clearInterval(id);
  }, []);

  return (
    <div className="mb-8 flex items-start gap-3">
      <span
        className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl text-lg font-extrabold text-white shadow-[0_4px_0_#4F46E5]"
        style={{ backgroundColor: slabIndigo }}
      >
        {step}
      </span>
      <div className="min-w-0 flex-1">
        <h3 className="text-xl font-extrabold text-[#3C3C3C] sm:text-2xl">
          System status banner
        </h3>
        <p className="mt-1 font-bold text-[#777777]">
          {Constants.APP_RELEASE_STATUS_BANNER_DESCRIPTION}
        </p>

        <div className="mt-6 rounded-2xl border-2 border-[#E5E5E5] bg-[#FAFAFA] p-4 sm:p-5">
          <label className="flex min-h-[44px] cursor-pointer items-start gap-3 select-none sm:items-center">
            <input
              type="checkbox"
              disabled={disabled}
              checked={enabled}
              onChange={(e) => onEnabledChange(e.target.checked)}
              className="mt-1 h-5 w-5 shrink-0 rounded border-2 border-[#E5E5E5] text-[#14B8A6] focus:ring-4 focus:ring-[#6366F1]/25 disabled:opacity-60 sm:mt-0"
            />
            <span>
              <span className="block text-sm font-extrabold uppercase tracking-wide text-[#3C3C3C]">
                Show status banner
              </span>
              <span className="mt-1 block text-sm font-semibold text-[#777777]">
                When on, clients show a dismissible banner at the top of the
                app.
              </span>
            </span>
          </label>
        </div>

        <div className="mt-6 flex flex-col gap-6">
          <label className={labelClass}>
            Banner message
            <textarea
              rows={3}
              disabled={pickerDisabled}
              value={message}
              onChange={(e) => onMessageChange(e.target.value)}
              placeholder={DEFAULT_BANNER_MESSAGE}
              className={`${inputClass} min-h-[5.5rem] resize-y py-3`}
            />
            <span className="normal-case font-semibold text-[#777777]">
              {Constants.APP_RELEASE_STATUS_BANNER_MESSAGE_DESCRIPTION}
            </span>
          </label>

          <label className={labelClass}>
            Banner color
            <div className="flex items-center gap-3">
              <input
                type="color"
                disabled={pickerDisabled}
                value={swatch}
                onChange={(e) => onColorChange(e.target.value.toUpperCase())}
                aria-label="Banner background color"
                className="h-12 w-14 shrink-0 cursor-pointer rounded-xl border-2 border-[#E5E5E5] bg-white p-1 disabled:cursor-not-allowed disabled:opacity-60"
              />
              <input
                type="text"
                disabled={pickerDisabled}
                value={color || swatch}
                onChange={(e) => onColorChange(e.target.value)}
                spellCheck={false}
                placeholder={DEFAULT_STATUS_BANNER_COLOR}
                className={`${inputClass} font-mono uppercase`}
              />
            </div>
            <span className="normal-case font-semibold text-[#777777]">
              Background color shown in the mobile app (`#RRGGBB`).
            </span>
          </label>

          <div>
            <p className={labelClass}>Live preview</p>
            <p className="mt-1 text-sm font-semibold text-[#777777]">
              {enabled
                ? "Approximate look on a phone-width screen before you save."
                : "Banner is off — preview only; clients won’t show it until enabled and saved."}
            </p>
            {/* ~390px ≈ phone; status bar mirrors device chrome for line-break accuracy. */}
            <div
              className="mt-3 w-full max-w-[390px] overflow-hidden rounded-2xl border-2 border-[#E5E5E5] shadow-[0_4px_0_#D0D0D0]"
              aria-live="polite"
            >
              <div
                className="relative flex h-8 items-center justify-between px-4"
                style={{ backgroundColor: swatch, color: previewText }}
              >
                <span className="text-[12px] font-bold tabular-nums tracking-tight">
                  {clock}
                </span>
                <span
                  className="absolute left-1/2 top-1/2 h-4 w-[72px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-black"
                  aria-hidden
                />
                <span className="flex items-center gap-1" aria-hidden>
                  <Signal className="h-3 w-3" strokeWidth={2.5} />
                  <Wifi className="h-3.5 w-3.5" strokeWidth={2.5} />
                  <BatteryFull className="h-3.5 w-3.5" strokeWidth={2.25} />
                </span>
              </div>
              <div
                className="flex items-start gap-2 pb-2 pl-3 pr-1 pt-2"
                style={{ backgroundColor: swatch, color: previewText }}
              >
                <Info
                  className="mt-0.5 h-[18px] w-[18px] shrink-0"
                  strokeWidth={2}
                  aria-hidden
                />
                <p className="min-w-0 flex-1 text-[13px] font-medium leading-[1.35]">
                  {previewMessage}
                </p>
                <span className="shrink-0 p-2 opacity-90" aria-hidden>
                  <X className="h-5 w-5" strokeWidth={2} />
                </span>
              </div>
              <div className="border-t border-[#E5E5E5] bg-white px-4 py-3">
                <div className="h-2.5 w-2/5 rounded-full bg-[#E5E5E5]" />
                <div className="mt-2 h-2 w-4/5 rounded-full bg-[#F0F0F0]" />
                <div className="mt-2 h-2 w-3/5 rounded-full bg-[#F0F0F0]" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
